import { faClockRotateLeft, faTimes } from "@fortawesome/pro-solid-svg-icons";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

import { Typography, NoStyleButton } from "@aviary";
import { TypeaheadResultsContainer } from "@shared/components/Typeahead/TypeaheadResultsContainer/TypeaheadResultsContainer";
import { useSearchHistoriesDeleteMutation } from "@shared/data/mutations/SearchHistoriesDelete/SearchHistoriesDelete.mutation";
import type {
  RecentCategoryType,
  RecentSearchesItem,
} from "@shared/data/queries/RecentSearchesQuery/RecentSearches.query";
import { useBreakpoints } from "@shared/hooks/useBreakpoints/useBreakpoints";
import { l } from "@shared/locales/i18n";
import { FontAwesomeIcon } from "@shared/react-fontawesome/react-fontawesome";

import * as styles from "./RecentSearches.styles";

interface Props {
  getRecentSearchesData: () => void;
  onRecentSearchItemClick: (search: RecentSearchesItem, recentSearchRank: number) => void;
  keyboardSelectedIndex: number;
  recentSearchesItems: RecentSearchesItem[];
}

const RecentSearches = ({
  getRecentSearchesData,
  onRecentSearchItemClick,
  keyboardSelectedIndex,
  recentSearchesItems,
}: Props) => {
  const { t } = useTranslation();
  const { tablet } = useBreakpoints();
  const isDesktop = tablet.greaterThan;

  const [deleteRecentSearchItem] = useSearchHistoriesDeleteMutation();

  useEffect(() => {
    getRecentSearchesData();
  }, []);

  if (recentSearchesItems.length === 0) {
    return null;
  }

  const getCategoryText = (filterableType: RecentCategoryType) => {
    switch (filterableType) {
      case "Product":
        return t(l.sharedCatalog.recentSearches.Products);
      case "Ingredient":
        return t(l.sharedCatalog.recentSearches.Ingredients);
      case "SupplementType":
        return t(l.sharedCatalog.recentSearches.SupplementTypes);
      case "Condition":
        return t(l.sharedCatalog.recentSearches.Condition);
      case "Brand":
        return t(l.sharedCatalog.recentSearches.Brands);
      default:
        return "";
    }
  };

  const getText = (search: RecentSearchesItem) => {
    if (!search.filterableType) {
      return (
        <>
          <Typography isMarginless customStyles={styles.text}>
            {search.query}
          </Typography>{" "}
          <Typography isMarginless isSubdued customStyles={styles.text}>
            <em>
              {t(l.sharedCatalog.recentSearches.In)}{" "}
              {t(l.sharedCatalog.recentSearches.AllCategories)}
            </em>
          </Typography>
        </>
      );
    }

    return (
      <>
        <Typography isMarginless customStyles={styles.text}>
          {search.filterableName}
        </Typography>{" "}
        <Typography isMarginless isSubdued customStyles={styles.text}>
          <em>
            {t(l.sharedCatalog.recentSearches.In)} {getCategoryText(search.filterableType)}
          </em>
        </Typography>
      </>
    );
  };

  const onDeleteRecentSearchItem = (item: RecentSearchesItem) => {
    deleteRecentSearchItem({
      variables: {
        input: {
          searchHistoryId: item.id,
        },
      },
      refetchQueries: ["RecentSearches_Shared_Query"],
    });
  };

  const renderRecentSearchItems = () =>
    recentSearchesItems.map((item: RecentSearchesItem, itemIndex: number) => {
      const itemStyle = [
        styles.row,
        keyboardSelectedIndex === itemIndex + 1 && styles.keyboardHover,
        isDesktop && styles.hideRemoveIcon,
      ];

      return (
        <div key={item.id} css={itemStyle}>
          <NoStyleButton
            css={styles.button}
            onClick={() => onRecentSearchItemClick(item, itemIndex + 1)}
          >
            <FontAwesomeIcon css={styles.icon} icon={faClockRotateLeft} />
            <div css={styles.textWrapper}>{getText(item)}</div>
          </NoStyleButton>

          <div css={styles.deleteWrapper} onClick={() => onDeleteRecentSearchItem(item)}>
            <FontAwesomeIcon icon={faTimes} />
          </div>
        </div>
      );
    });

  return <TypeaheadResultsContainer>{renderRecentSearchItems()}</TypeaheadResultsContainer>;
};

export { RecentSearches };
